// import { ReactLocalization } from "@fluent/react";
class ReactLocalization { };

class EventsHub extends EventTarget { }

interface PreselectProps
{
    // Map<number, null> if its API weren’t weird (method-oriented, no syntax sugar)
    bookIds: { [id: number]: null };
    categoryIds: { [id: number]: null };
    externalCategoryIds: { [id: string]: null };
}

interface ISharedData
{
    initialized: boolean;
}

class ReactTreeSelectAdapter
{
    // to preselect chosen books or categories
    private _preselect: PreselectProps;

    // to receive data in the consumer (outbound messages) and maybe also a
    // common exchange
    private _eventsHub: EventTarget;

    private _l10n: ReactLocalization;

    // to send data to ReactTreeSelect only (inbound messages)
    private _sentEventsHub: EventTarget;

    private _sharedData: ISharedData;

    private _enableFilter: boolean;

    private _legacy: boolean;

    // whether to show show and use (group) labels next to categories and books
    // NOTE: the original implementation only shows a clickable star, which is
    //       empty when no labels are attached to the item, and filled when they
    //       are
    private _enableFavorites: boolean;

    private _openAtStart: boolean;

    constructor(
        eventsHub: EventTarget,
        l10n: ReactLocalization,
        enableFilter: boolean,
        enableLegacyImplementation: boolean,
        enableFavorites: boolean,
        openAtStart: boolean,
    )
    {
        this._preselect = {
            bookIds: {},
            categoryIds: {},
            externalCategoryIds: {},
        };

        this._eventsHub = eventsHub;
        this._l10n = l10n;

        // this._sentEventsHub = new EventsHub();
        this._sentEventsHub = new EventTarget();

        this._sharedData = {
            initialized: false,
        };

        this._enableFilter = enableFilter;
        this._legacy = enableLegacyImplementation;
        this._enableFavorites = enableFavorites;
        this._openAtStart = openAtStart;
    }

    // inbound messages (from the consumer to ReactTreeSelect)
    public sendMessage(type: string, data: any) // but likely often an object {}
    {
        const event = new CustomEvent(
            'onMessage', {
                detail: {
                    type,
                    payload: data,
                },
            },
        );

        this._sentEventsHub.dispatchEvent(event);
    }

    // from ReactTreeSelect to the consumer
    public sendOutboundMessage(type: string, data: any) // but likely often an object {}
    {
        const event = new CustomEvent(
            'onMessage', {
                detail: {
                    type,
                    payload: data,
                },
            },
        );

        this._eventsHub.dispatchEvent(event);
    }

    public sendFullState(
        books: { [id: number]: DropDownBook },
        categories: { [id: number]: DropDownCategory },
    )
    {
        const booksState = Object.values(books).map(
            (book) => ({
                id: book.id,
                selected: book.selected,
            })).map(
                (book) => [book.id, book]);

        const categoriesState = Object.values(categories).map(
            (category) => ({
                id: category.id,
                selected: category.selected,
            })).map(
                (category) => [category.id, category]);

        this.sendOutboundMessage('updateState', {
            books: Object.fromEntries(booksState),
            categories: Object.fromEntries(categoriesState),
        });
    }

    public preselect(data)
    {
        this._preselect = {
            bookIds: Object.fromEntries(new Map(
                (data.bookIds ?? [])
                .map((id: number) => [id, null])
            )),

            categoryIds: Object.fromEntries(new Map(
                (data.categoryIds ?? [])
                .map((id: number) => [id, null])
            )),

            externalCategoryIds: Object.fromEntries(new Map(
                (data.externalCategoryIds ?? [])
                .map((id: string) => [id, null])
            )),
        };
    }

    // DONE: send a message if initialized, preselect if not
    public setSelected(data: {
        additive: boolean; // if not true, reset/unselect everything first
        bookIds: Array<number>;
        categoryIds: Array<number>;
        externalCategoryIds: Array<string>;
    })
    {
        const initialized = this._sharedData.initialized;

        if (!initialized) {
            this.preselect(data);
            return;
        }

        this.sendMessage('setSelected', data);
    }

    // check/select and open the modern dictionaries category
    public patchData(response)
    {
        const preselect = this._preselect;

        // TODO: { [id: number]: DropDownBook }
        const books: Array<DropDownBook> = response.books;
        const categories: Array<DropDownCategory> = response.categories;

        if (Object.keys(preselect.categoryIds).length ||
            Object.keys(preselect.externalCategoryIds).length
        ) {
            for (let category of categories) {
                if (category.id in preselect.categoryIds ||
                    category.externalId in preselect.externalCategoryIds
                ) {
                    category.selected = true;
                    category.isOpen = true;
                }
            }
        }

        if (Object.keys(preselect.bookIds).length) {
            for (let book of books) {
                if (book.id in preselect.bookIds) {
                    book.selected = true;
                }
            }
        }
    }

    public renderNewBooksSelect()
    {
        const dropdownSelectDiv = document.querySelector(
            '#dropdownSelectDiv');

        const root = ReactDOM.createRoot(dropdownSelectDiv);

        root.render(
            // <ReactTreeSelectWrapper />,
            React.createElement(ReactTreeSelectWrapper, {
                openAtStart: this._openAtStart,
                adapter: this,
                eventsHub: this._eventsHub,
                inboundEventsHub: this._sentEventsHub,
                // NOTE: we already share a reference to us but this is more
                //       explicit and limited
                sharedData: this._sharedData,
                l10n: this._l10n,
                enableFilter: this._enableFilter,
                legacy: this._legacy,
                enableFavorites: this._enableFavorites,
            }, null)
        );
    }
}

export { EventsHub, ReactTreeSelectAdapter, };
